import type {
  GetInquiriesResponse,
  GetInquiryActionLogsResponse,
  GetInquiryAssignsResponse,
  GetInquiryCoordinationsResponse,
  GetInquirySalesResponse,
  InquiryAssignResponse,
  InquiryCoordinationResponse,
} from 'backend/types'
import { apiFetch } from '..'

/**
 * 引合一覧取得API
 *
 * @param params - クエリパラメータ
 * @returns 引合一覧レスポンス
 * @throws ApiError
 */
export async function getInquiries(params?: {
  offset?: number
  limit?: number
  division_id?: number
  is_include_related_division?: boolean
}): Promise<GetInquiriesResponse> {
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }
  if (params?.division_id !== undefined) {
    queryParams.append('division_id', params.division_id.toString())
  }
  if (params?.is_include_related_division !== undefined) {
    queryParams.append('is_include_related_division', params.is_include_related_division.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/inquiries${queryString ? `?${queryString}` : ''}`

  return apiFetch<GetInquiriesResponse>(url, { method: 'GET' })
}

/**
 * 調整状況一覧取得API
 *
 * @param inquiryId - 引合ID
 * @param params - クエリパラメータ
 * @returns 調整状況一覧レスポンス
 * @throws ApiError
 */
export async function getInquiryCoordinations(
  inquiryId: number,
  params?: {
    offset?: number
    limit?: number
  },
): Promise<GetInquiryCoordinationsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/inquiry/${inquiryId}/inquiry-coordinations${queryString ? `?${queryString}` : ''}`

  return apiFetch<GetInquiryCoordinationsResponse>(url, { method: 'GET' })
}

/**
 * 調整状況作成APIのリクエスト型
 */
export type CreateInquiryCoordinationRequest = {
  memberId: number
  content: string
}

/**
 * 調整状況更新APIのリクエスト型
 */
export type UpdateInquiryCoordinationRequest = {
  content: string
}

/**
 * 調整状況作成API
 *
 * @param inquiryId - 引合ID
 * @param data - リクエストボディ
 * @returns 作成した調整状況
 * @throws ApiError
 */
export async function createInquiryCoordination(
  inquiryId: number,
  data: CreateInquiryCoordinationRequest,
): Promise<InquiryCoordinationResponse> {
  return apiFetch<InquiryCoordinationResponse>(`/api/inquiry/${inquiryId}/inquiry-coordination`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 調整状況更新API
 *
 * @param inquiryId - 引合ID
 * @param inquiryCoordinationId - 調整状況ID
 * @param data - リクエストボディ
 * @returns 更新した調整状況
 * @throws ApiError
 */
export async function updateInquiryCoordination(
  inquiryId: number,
  inquiryCoordinationId: number,
  data: UpdateInquiryCoordinationRequest,
): Promise<InquiryCoordinationResponse> {
  return apiFetch<InquiryCoordinationResponse>(
    `/api/inquiry/${inquiryId}/inquiry-coordination/${inquiryCoordinationId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  )
}

/**
 * 調整状況削除API
 *
 * @param inquiryId - 引合ID
 * @param inquiryCoordinationId - 調整状況ID
 * @throws ApiError
 */
export async function deleteInquiryCoordination(inquiryId: number, inquiryCoordinationId: number): Promise<void> {
  await apiFetch<void>(`/api/inquiry/${inquiryId}/inquiry-coordination/${inquiryCoordinationId}`, {
    method: 'DELETE',
  })
}

/**
 * 売上情報一覧取得API
 *
 * @param inquiryId - 引合ID
 * @param params - クエリパラメータ
 * @returns 売上情報一覧レスポンス
 * @throws ApiError
 */
export async function getInquirySales(
  inquiryId: number,
  params?: {
    offset?: number
    limit?: number
  },
): Promise<GetInquirySalesResponse> {
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/inquiry/${inquiryId}/sales${queryString ? `?${queryString}` : ''}`

  return apiFetch<GetInquirySalesResponse>(url, { method: 'GET' })
}

/**
 * 引合アサイン作成APIのリクエスト型
 */
export type CreateInquiryAssignRequest = {
  memberId?: number | null
  roleId?: number | null
  unitPrice: number
  assignedOn: string
  assignPersonMonth: number
  salesStatusId?: number | null
  inquiryAssignStatusId?: number
  isOnsite?: boolean
  isOffsite?: boolean
  isRemote?: boolean
  notes?: string | null
}

/**
 * 引合アサイン更新APIのリクエスト型
 */
export type UpdateInquiryAssignRequest = {
  memberId?: number | null
  roleId?: number | null
  unitPrice?: number
  assignedOn?: string
  assignPersonMonth?: number
  salesStatusId?: number | null
  inquiryAssignStatusId?: number
  isOnsite?: boolean
  isOffsite?: boolean
  isRemote?: boolean
  notes?: string | null
}

/**
 * 引合アサイン一覧取得API
 *
 * @param inquiryId - 引合ID
 * @param params - クエリパラメータ
 * @returns 引合アサイン一覧レスポンス
 * @throws ApiError
 */
export async function getInquiryAssigns(
  inquiryId: number,
  params?: {
    offset?: number
    limit?: number
  },
): Promise<GetInquiryAssignsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/inquiry/${inquiryId}/inquiry-assigns${queryString ? `?${queryString}` : ''}`

  return apiFetch<GetInquiryAssignsResponse>(url, { method: 'GET' })
}

/**
 * 引合アクションログ一覧取得API
 *
 * @param inquiryId - 引合ID
 * @param params - クエリパラメータ
 * @returns 引合アクションログ一覧レスポンス
 * @throws ApiError
 */
export async function getInquiryActionLogs(
  inquiryId: number,
  params?: {
    offset?: number
    limit?: number
  },
): Promise<GetInquiryActionLogsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/inquiry/${inquiryId}/action-logs${queryString ? `?${queryString}` : ''}`

  return apiFetch<GetInquiryActionLogsResponse>(url, { method: 'GET' })
}

/**
 * 引合アサイン作成API
 *
 * @param inquiryId - 引合ID
 * @param data - リクエストボディ
 * @returns 作成した引合アサイン
 * @throws ApiError
 */
export async function createInquiryAssign(
  inquiryId: number,
  data: CreateInquiryAssignRequest,
): Promise<InquiryAssignResponse> {
  return apiFetch<InquiryAssignResponse>(`/api/inquiry/${inquiryId}/inquiry-assign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 引合アサイン更新API
 *
 * @param inquiryId - 引合ID
 * @param inquiryAssignId - 引合アサインID
 * @param data - リクエストボディ
 * @returns 更新した引合アサイン
 * @throws ApiError
 */
export async function updateInquiryAssign(
  inquiryId: number,
  inquiryAssignId: number,
  data: UpdateInquiryAssignRequest,
): Promise<InquiryAssignResponse> {
  return apiFetch<InquiryAssignResponse>(`/api/inquiry/${inquiryId}/inquiry-assign/${inquiryAssignId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 引合アサイン削除API
 *
 * @param inquiryId - 引合ID
 * @param inquiryAssignId - 引合アサインID
 * @throws ApiError
 */
export async function deleteInquiryAssign(inquiryId: number, inquiryAssignId: number): Promise<void> {
  await apiFetch<void>(`/api/inquiry/${inquiryId}/inquiry-assign/${inquiryAssignId}`, {
    method: 'DELETE',
  })
}
